import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { ConfirmModal } from "../../../../shared/components/SharedUI";
import invoiceStyles from "../../invoices/styles/invoice.module.css";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import ImportExportBar from "../../shared/ImportExportBar";

// ── Estilos del modal — theme-aware ──────────────────────────────────────────
const OVERLAY = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  backdropFilter: "blur(3px)", zIndex: 1000,
};
const MODAL = {
  position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
  background: "var(--bg-card)", border: "1px solid var(--border-card)",
  borderRadius: 12, padding: 24,
  width: "min(500px,94vw)", zIndex: 1001,
  boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
  maxHeight: "90vh", overflowY: "auto",
};
const LABEL = {
  display: "block", fontSize: "0.78rem", fontWeight: 600,
  color: "var(--text-secondary)", marginBottom: 5,
};
const INP = {
  width: "100%", background: "var(--bg-input)", border: "1px solid var(--border-input)",
  borderRadius: 8, padding: "9px 12px", color: "var(--text-primary)", fontSize: "0.9rem",
  outline: "none", boxSizing: "border-box", transition: "border-color 0.15s",
};

// ── Modal Ver detalle — theme-aware ───────────────────────────────────────────
function VerModal({ r, onClose, t }) {
  const fac    = r.factura_cxp;
  const prov   = fac?.cuenta_proveedor?.nombre ?? "—";
  const banco  = r.cuenta_bancaria?.banco ?? "—";
  const docProv = fac?.numero_doc_proveedor || "—";
  const fmt = (n) => Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
  const rows = [
    [t("recibos.numRecibo"),           r.numero_recibo],
    [t("cxp.proveedor"),               prov],
    [t("cxp.facturaProveedor"),        docProv],
    ["N° Factura (int.)",              fac?.numero_factura ?? "—"],
    [t("recibos.fechaPago"),           r.fecha_pago],
    [t("recibos.metodoPago"),          r.metodo_pago],
    [t("recibos.cuentaBancaria"),      `${banco} — ${r.cuenta_bancaria?.numero_cuenta ?? ""}`],
    [t("common.referencia"),           r.documento || "—"],
    [t("cxp.montoPagado"),             `₡${fmt(r.monto)}`],
  ];
  return (
    <>
      <div onClick={onClose} style={OVERLAY}/>
      <div style={MODAL}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 16, paddingBottom: 12,
          borderBottom: "1px solid var(--border-card)",
        }}>
          <div>
            <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
              <i className="fa-solid fa-eye" style={{ color: "#60a5fa", marginRight: 8 }}/>
              {t("cxp.detalleRecibo")}
            </h3>
            <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>
              {r.numero_recibo}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: "1.2rem" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>
        {rows.map(([label, val], i) => (
          <div key={label} style={{
            display: "flex", justifyContent: "space-between",
            padding: "8px 4px", borderBottom: "1px solid var(--border-card)",
            background: i % 2 === 0 ? "var(--hover-bg)" : "transparent",
          }}>
            <span style={{ color: "var(--text-secondary)", fontSize: "0.83rem" }}>{label}</span>
            <span style={{ color: "var(--text-primary)", fontSize: "0.83rem", fontWeight: 600,
              textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>{val}</span>
          </div>
        ))}
        {fac && (
          <div style={{
            marginTop: 14, padding: "10px 12px",
            background: "var(--hover-bg)", borderRadius: 6, border: "1px solid var(--border-card)",
            fontFamily: "monospace", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.9,
          }}>
            <span style={{ color: "#f87171" }}>DEBE</span>{"  "}
            {fac.cuenta_proveedor?.nombre ?? t("cxp.proveedor")}<br/>
            <span style={{ color: "#60a5fa" }}>HABER</span>{"  "}
            {r.cuenta_bancaria?.banco ?? "Banco"}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={{
            padding: "8px 20px",
            background: "rgba(96,165,250,0.15)", border: "1px solid rgba(96,165,250,0.4)",
            borderRadius: 7, color: "#60a5fa", fontSize: "0.84rem",
            fontWeight: 600, cursor: "pointer",
          }}>
            {t("common.cerrar")}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Modal Editar — theme-aware ────────────────────────────────────────────────
function EditModal({ r, onClose, onSaved, t }) {
  const [cuentas, setCuentas] = useState([]);
  const [form, setForm] = useState({
    fecha_pago:      r.fecha_pago,
    metodo_pago:     r.metodo_pago,
    documento:       r.documento ?? "",
    descripcion:     r.descripcion ?? "",
    cuenta_bancaria: r.cuenta_bancaria?.id ?? r.cuenta_bancaria ?? "",
  });
  const cuentaOriginalId = r.cuenta_bancaria?.id ?? r.cuenta_bancaria;
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then(r => r.json())
      .then(d => setCuentas(d.results ?? d))
      .catch(() => {});
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.fecha_pago)        { setError("La fecha es obligatoria."); return; }
    if (!form.documento.trim())  { setError("La referencia es obligatoria."); return; }
    setSaving(true); setError("");
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${r.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          fecha_pago:      form.fecha_pago,
          metodo_pago:     form.metodo_pago,
          documento:       form.documento,
          descripcion:     form.descripcion,
          cuenta_bancaria: Number(form.cuenta_bancaria),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Error al actualizar el recibo.");
      }
      onSaved("Recibo actualizado correctamente.");
      onClose();
    } catch (ex) { setError(ex.message); }
    finally { setSaving(false); }
  }

  const field = (label, children) => (
    <div>
      <label style={LABEL}>{label}</label>
      {children}
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={OVERLAY}/>
      <div style={MODAL}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 16, paddingBottom: 12,
          borderBottom: "1px solid var(--border-card)",
        }}>
          <div>
            <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
              <i className="fa-solid fa-square-pen" style={{ color: "#f97316", marginRight: 8 }}/>
              {t("cxp.editarRecibo")}
            </h3>
            <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>
              {r.numero_recibo} · Original ₡{Number(r.monto).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: "1.2rem" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        {/* Monto fijo — no editable */}
        <div style={{
          padding: "8px 12px", background: "var(--hover-bg)",
          border: "1px solid var(--border-card)", borderRadius: 8, marginBottom: 4,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ color: "var(--text-secondary)", fontSize: "0.8rem" }}>
            {t("cxp.montoNoModificable")}
          </span>
          <span style={{ color: "var(--text-primary)", fontWeight: 700, fontFamily: "monospace" }}>
            ₡{Number(r.monto).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          {field(`${t("recibos.fechaPago")} *`,
            <input type="date" style={INP} value={form.fecha_pago}
              onChange={e => set("fecha_pago", e.target.value)}
              onFocus={e => e.target.style.borderColor="#f97316"}
              onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
          )}
          {field(t("recibos.metodoPago"),
            <select style={{ ...INP, cursor: "pointer" }} value={form.metodo_pago}
              onChange={e => set("metodo_pago", e.target.value)}>
              <option value="TRANSFERENCIA">{t("recibos.transferencia")}</option>
              <option value="EFECTIVO">{t("recibos.efectivo")}</option>
              <option value="CHEQUE">{t("recibos.cheque")}</option>
            </select>
          )}
          <div style={{ gridColumn: "span 2" }}>
            {field(t("recibos.cuentaBancaria"),
              <>
                <select style={{ ...INP, cursor: "pointer" }} value={form.cuenta_bancaria}
                  onChange={e => set("cuenta_bancaria", e.target.value)}>
                  {cuentas.map(c => (
                    <option key={c.id} value={c.id}>{c.banco} — {c.numero_cuenta}</option>
                  ))}
                </select>
                {String(form.cuenta_bancaria) !== String(cuentaOriginalId) && (
                  <div style={{
                    marginTop: 5, padding: "6px 10px",
                    background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)",
                    borderRadius: 6, color: "#fbbf24", fontSize: "0.78rem",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <i className="fa-solid fa-triangle-exclamation"/>
                    {t("cxp.asientoRevertido")}
                  </div>
                )}
              </>
            )}
          </div>
          <div style={{ gridColumn: "span 2" }}>
            {field(`${t("cxp.referenciaDoc")} *`,
              <input type="text" style={INP} value={form.documento}
                placeholder="N° transferencia, cheque, etc."
                onChange={e => set("documento", e.target.value)}
                onFocus={e => e.target.style.borderColor="#f97316"}
                onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
            )}
          </div>
          <div style={{ gridColumn: "span 2" }}>
            {field(
              <span>
                {t("common.descripcion")}{" "}
                <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "0.75rem" }}>
                  ({t("common.opcional")} — anexo bancario)
                </span>
              </span>,
              <input type="text" style={INP} value={form.descripcion}
                placeholder="Texto para el anexo bancario"
                onChange={e => set("descripcion", e.target.value)}
                onFocus={e => e.target.style.borderColor="#f97316"}
                onBlur={e  => e.target.style.borderColor="var(--border-input)"}/>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: "10px 14px",
            background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 8, color: "#f87171", fontSize: "0.84rem",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <i className="fa-solid fa-circle-exclamation"/>{error}
          </div>
        )}

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16,
          paddingTop: 12, borderTop: "1px solid var(--border-card)",
        }}>
          <button onClick={onClose} disabled={saving} style={{
            padding: "8px 16px",
            background: "transparent", border: "1px solid var(--border-card)",
            borderRadius: 7, color: "var(--text-secondary)", fontSize: "0.84rem", cursor: "pointer",
          }}>
            {t("common.cancelar")}
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 20px",
            background: saving ? "rgba(249,115,22,0.5)" : "#f97316",
            color: "#fff", border: "none", borderRadius: 7, fontSize: "0.84rem",
            fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
              : <><i className="fa-solid fa-floppy-disk"/> {t("cxp.guardarCambios")}</>}
          </button>
        </div>
      </div>
    </>
  );
}

/** "2026-04-15" → "15/04/2026" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

// ── ActionButtons ─────────────────────────────────────────────────────────────
function ActionButtons({ onVer, onEdit, onDelete, t }) {
  const btn = (onClick, bg, border, color, icon, title) => (
    <button type="button" onClick={onClick} title={title} style={{
      background: bg, border: `1px solid ${border}`,
      borderRadius: 6, padding: "4px 10px", color, cursor: "pointer",
    }}>
      <i className={`fa-solid ${icon}`}/>
    </button>
  );
  return (
    <div className="d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
      {onVer    && btn(onVer,    "rgba(96,165,250,0.1)",  "rgba(96,165,250,0.3)",  "#60a5fa", "fa-eye",        t("cxp.verDetalle"))}
      {onEdit   && btn(onEdit,   "rgba(249,115,22,0.1)",  "rgba(249,115,22,0.3)",  "#f97316", "fa-square-pen", t("common.editar"))}
      {onDelete && btn(onDelete, "rgba(239,68,68,0.1)",   "rgba(239,68,68,0.25)", "#f87171", "fa-trash",      t("common.eliminar"))}
    </div>
  );
}

export default function ListReciboCxP() {
  const { t } = useTranslation();
  const [items,        setItems]        = useState([]);
  const [count,        setCount]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState("");
  const [loading,      setLoading]      = useState(true);
  const [deleting,     setDeleting]     = useState(null);
  const [delLoad,      setDelLoad]      = useState(false);
  const [actionMsg,    setActionMsg]    = useState(null);
  const [viendo,       setViendo]       = useState(null);
  const [editando,     setEditando]     = useState(null);
  const [importResult, setImportResult] = useState(null);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (search.trim()) qs.set("search", search.trim());
    apiFetch(`/api/finance/reciboCxP/?${qs}`)
      .then(r => r.json())
      .then(d => { setItems(d.results ?? d ?? []); setCount(d.count ?? (d.results ?? d ?? []).length); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    fetchData();
  }, [fetchData]);

  function handleImportResult(resultado) {
    setImportResult(resultado);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDelLoad(true);
    try {
      const res = await apiFetch(`/api/finance/reciboCxP/${deleting.id}/`, { method: "DELETE" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Error al eliminar");
      }
      setDeleting(null);
      showMsg(true, `Recibo ${deleting.numero_recibo} eliminado correctamente.`);
      setItems(prev => prev.filter(i => i.id !== deleting.id));
      setCount(c => c - 1);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDelLoad(false);
    }
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={invoiceStyles.inactive}>
        <Link to="/">
          <span className={invoiceStyles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={invoiceStyles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <span className={invoiceStyles.active}> {t("sidebar.recibos")}</span>
      </span>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom: 6 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("cxp.gestionRecibos")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("cxp.administraRecibos")}</p>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <ImportExportBar
            plantillaUrl="/api/finance/reciboCxP/plantilla-excel/"
            importarUrl="/api/finance/reciboCxP/importar-excel/"
            plantillaFilename="plantilla_recibos_cxp.xlsx"
            onImportSuccess={handleImportSuccess}
            onResult={handleImportResult}
          />
          <Link to="/finanzas/cxp/recibos/agregar" className="btn"
            style={{
              padding: "9px 18px", background: "#f97316", borderRadius: 6,
              color: "#fff", textDecoration: "none", fontWeight: 600,
              fontSize: 14, display: "flex", alignItems: "center", gap: 7, border: "none",
            }}>
            <i className="fa-solid fa-plus" /> {t("cxp.nuevoPago")}
          </Link>
        </div>
      </div>

      {/* Resultado de importación */}
      {importResult && (
        <div style={{
          width: "100%", borderRadius: 8, marginBottom: 14,
          background: importResult.ok ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)",
          border: `1px solid ${importResult.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
          borderLeft: `4px solid ${importResult.ok ? "#4ade80" : "#f87171"}`,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 16px",
            borderBottom: importResult.errores?.length > 0 ? "1px solid var(--border-card)" : "none",
          }}>
            <span style={{ fontWeight: 600, fontSize: "0.88rem",
              color: importResult.ok ? "#bbf7d0" : "#fecaca" }}>
              {importResult.msg}
            </span>
            <button onClick={() => setImportResult(null)}
              style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "1rem" }}>✕</button>
          </div>
          {importResult.errores?.length > 0 && (
            <div style={{ padding: "10px 16px" }}>
              <details>
                <summary style={{ cursor: "pointer", color: "#fb923c", fontWeight: 600, fontSize: "0.82rem" }}>
                  {importResult.errores.length} {t("recibos.filasConErrores")} — clic para ver
                </summary>
                <ul style={{ marginTop: 8, paddingLeft: 18, color: "#fca5a5", fontSize: "0.8rem" }}>
                  {importResult.errores.map((e, i) => (
                    <li key={i}>
                      <strong>Fila {e.fila}:</strong>{" "}
                      {e.errores?.join(" · ") ?? e}
                      {e.advertencia && <span style={{ color: "#fbbf24", marginLeft: 6 }}>(advertencia)</span>}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px",
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
             style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }} />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontWeight: 600 }}>
            {actionMsg.text}
          </span>
        </div>
      )}

      {/* Tabla */}
      <div className={invoiceStyles["table-container"]}>

        {/* Barra búsqueda */}
        <div className={`${invoiceStyles["table-header"]} d-flex justify-content-between`}>
          <div className={`${invoiceStyles["input-header"]} d-flex gap-2`}>
            <i className="fa-solid fa-magnifying-glass"
               style={{ color: loading ? "#f97316" : undefined, transition: "color 0.2s" }} />
            <input type="text" placeholder={t("cxp.buscarReciboPlaceholder")}
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} />
            {search && (
              <button onClick={() => { setSearch(""); setPage(1); }}
                style={{ background: "none", border: "none", color: "var(--text-muted)",
                  cursor: "pointer", fontSize: "0.8rem" }}>✕</button>
            )}
          </div>
        </div>

        {/* Tabla */}
        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("recibos.numRecibo")}</th>
                <th>{t("cxp.proveedorBanco")}</th>
                <th>{t("cxp.facturaProveedor")}</th>
                <th>{t("common.fecha")}</th>
                <th><div style={{ textAlign: "right" }}>{t("common.monto")}</div></th>
                <th className="text-center">{t("common.acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin"
                     style={{ marginRight: 8, color: "#f97316" }} />
                  {t("cxp.cargandoRecibos")}
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }} />
                  {t("cxp.sinRecibos")}.{" "}
                  <Link to="/finanzas/cxp/recibos/agregar" style={{ color: "#f97316" }}>
                    {t("cxp.registrarPrimero")}
                  </Link>
                </td></tr>
              ) : items.map(r => {
                const fac   = r.factura_cxp;
                const prov  = fac?.cuenta_proveedor?.nombre ?? "—";
                const banco = r.cuenta_bancaria?.banco ?? "—";
                const rawDoc = fac?.numero_doc_proveedor?.trim() || "";
                const docProv = (rawDoc && rawDoc !== fac?.numero_factura) ? rawDoc : "—";

                return (
                  <tr key={r.id}>
                    {/* N° Recibo */}
                    <td style={{ fontFamily: "monospace", fontWeight: 600, color: "#60a5fa" }}>
                      {r.numero_recibo}
                    </td>

                    {/* Proveedor arriba, banco abajo */}
                    <td>
                      <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{prov}</span>
                      <span style={{ display: "block", fontSize: "0.75rem",
                        color: "var(--text-secondary)", marginTop: 1 }}>
                        {banco}
                      </span>
                    </td>

                    {/* Factura: NDocProveedor arriba, CP-XXXXX abajo */}
                    <td>
                      <span style={{ fontFamily: "monospace", fontWeight: 700,
                        color: "var(--text-primary)", fontSize: "0.9rem" }}>
                        {docProv}
                      </span>
                      {fac?.numero_factura && (
                        <span style={{ display: "block", fontSize: "0.73rem",
                          color: "var(--text-muted)", fontFamily: "monospace",
                          fontWeight: 400, marginTop: 2 }}>
                          {fac.numero_factura}
                        </span>
                      )}
                    </td>

                    {/* Fecha DD/MM/YYYY */}
                    <td>{fmtDate(r.fecha_pago)}</td>

                    {/* Monto */}
                    <td>
                      <div className="monto-typo" style={{ fontWeight: 600, color: "#f87171", textAlign: "right" }}>
                        ₡{formatCurrency(r.monto)}
                      </div>
                    </td>

                    {/* Acciones */}
                    <td className="text-center">
                      <ActionButtons t={t}
                        onVer={() => setViendo(r)}
                        onEdit={() => setEditando(r)}
                        onDelete={() => setDeleting(r)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("cxp.recibos")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {viendo   && <VerModal  r={viendo}  t={t} onClose={() => setViendo(null)} />}
      {editando && <EditModal r={editando} t={t} onClose={() => setEditando(null)}
        onSaved={(msg) => {
          setActionMsg({ ok: true, text: msg });
          setTimeout(() => setActionMsg(null), 4000);
          setItems(prev => prev.map(i =>
            i.id === editando.id ? { ...i, ...editando } : i
          ));
          fetchData();
        }} />}

      <ConfirmModal
        open={!!deleting}
        name={`Recibo ${deleting?.numero_recibo}`}
        message={`Se eliminará el pago de ₡${Number(deleting?.monto || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} y se revertirá el saldo de la CxP y la cuenta bancaria.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
        loading={delLoad}
      />
    </>
  );
}
